import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse, IPaginated, ITrainingSession } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { newDateFromUTC, newDateToUTC } from '@bc-library/common'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import type { IGetOneTrainingSession, INewTrainingSessionPage } from '@/services/trainingSession/trainingSessionInterface'
import { afterReceiving, beforeSending, beforeSendingComment, afterReceivingCommentResponse } from '@/services/trainingSession/hooks'
import { afterReceiving as afterReceivingTraining } from '@/services/training/hooks'
import { useFormValues } from 'vee-validate'

const resource = 'trainingSessions'

const getOne = (axios: IFactoryFunctionRequest<IGetOneTrainingSession, undefined>) => (trainingSessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${trainingSessionId}`,
    afterReceivingHook: afterReceiving,
  })
}
const getAll = (axios: IFactoryFunctionRequest) => (trainingId: string): IFactoryResponse<Array<ITrainingSession>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/training/${trainingId}`,
    afterReceivingHook: afterReceiving,
  })
}

const updateOne = (axios: IFactoryFunctionRequest) => (training: ITrainingSession): IFactoryResponse<ITrainingSession> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${training._id}`,
    data: { ...training },
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const create = (axios: IFactoryFunctionRequest<ITrainingSession, INewTrainingSessionPage>) => (trainingSession: INewTrainingSessionPage) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data: trainingSession,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const updateParticipant = (axios: IFactoryFunctionRequest<null, any>) => (payload: { participant: ITrainingSession['participants'][0]; sessionId: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/participant/update/${payload.sessionId}/${payload.participant._id}`,
    data: payload.participant,
    afterReceivingHook: afterReceiving,
  })
}
const updateCheckItem = (axios: IFactoryFunctionRequest<null, any>) => (payload: { check: ITrainingSession['checklist'][0]; sessionId: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/checklist/update/${payload.sessionId}/${payload.check._id}`,
    data: payload.check,
    afterReceivingHook: afterReceiving,
  })
}
const addCheckItem = (axios: IFactoryFunctionRequest<null, any>) => (payload: { check: Pick<ITrainingSession['checklist'][0], 'display'>; sessionId: string }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/checklist/add/${payload.sessionId}`,
    data: payload.check,
    afterReceivingHook: afterReceiving,
  })
}

const retreiveSessionsStats = (axios: IFactoryFunctionRequest<any, any>) => ({startDate, endDate}: {startDate: string; endDate: string}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/stats`,
    query: {
      startDate,
      endDate
    },
    afterReceivingHook: (data) => {
      return {
        ...data,
        passedSession: afterReceiving(data.passedSession),
        plannedSession: afterReceiving(data.plannedSession),
        resourceValidated: data.resourceValidated.map(resource => {
          return {
            ...resource,
            sessions: afterReceiving(resource.sessions)
          }
        }),
        trainingDaysForecast: data.trainingDaysForecast && {
          ...data.trainingDaysForecast,
          resources: data.trainingDaysForecast.resources.map(row => ({
            ...row,
            sessions: row.sessions.map(session => ({
              ...session,
              startDate: session.startDate && newDateFromUTC(session.startDate),
            })),
          })),
        },
      }
    },
  })
}

const sendReminder = (axios: IFactoryFunctionRequest<null, any>) => (payload: { participants: IGetOneTrainingSession['participants'] & {
  trainingSessionId: string
  display: string
  _firmId: string
  sender: {
    firstname: string
    lastname: string
    _id: string
  }
} }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/sendReminder`,
    data: payload.participants,
  })
}

const sendTrainingMessage = (axios: IFactoryFunctionRequest<any, any>) => (payload: { display: string; html: string; participants: { userId: { email: string } }[]; documents: { fileName: string; fileType: string }[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendMessage`,
    data: payload,
    immediate: false,
  })
}


const evaluationIntervalForResource = (axios: IFactoryFunctionRequest<any, any>) => ({resourceId, startDate, endDate}) => {
  const utcStartDate = newDateToUTC(startDate).toISOString()
  const utcEndDate = newDateToUTC(endDate).toISOString()
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/interval/evaluation/${resourceId}/${utcStartDate}/${utcEndDate}`,
    afterReceivingHook: (data) => {
      return {
        trainingPlanned: afterReceivingTraining(data.trainingPlanned),
        trainingSessions: afterReceiving(data.trainingSessions),
      }
    },
  })
}
const inTheFuture = (axios: IFactoryFunctionRequest<any, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/inTheFuture`,
    afterReceivingHook: afterReceiving,
  })
}

const myTrainings = (axios: IFactoryFunctionRequest<any, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/myTrainings`,
    afterReceivingHook: afterReceiving,
  })
}

const resourceProfileTimeline = (axios: IFactoryFunctionRequest<IPaginated<any>, any>) => ({ resourceId, role, page = 0, limit = 10 }: { resourceId: string; role?: 'attendee' | 'trainer'; page?: number; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resource/${resourceId}/profileTimeline`,
    query: {
      role,
      page,
      limit,
    },
    afterReceivingHook: afterReceiving,
  })
}

const retreiveTrainingEvaluation = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/session/${sessionId}/evaluation/${resourceId}`,
    afterReceivingHook: afterReceiving,
  })
}

const fillInTrainingSessionEvaluation = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, resourceId: string, form: any, comment: string, options: {closeEvaluation?: boolean} = {}) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/session/${sessionId}/evaluation/${resourceId}`,
    data: {
      form,
      comment,
      ...options,
    },
    afterReceivingHook: afterReceiving,
  })
}

const trainingSessionCalendar = (axios: IFactoryFunctionRequest<any, any>) => (startDate: string, endDate: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/calendar`,
    query: {
      startDate,
      endDate
    },
    afterReceivingHook: afterReceiving,
  })
}

const getSessionModalInformation = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/modalInformation/${sessionId}`,
    afterReceivingHook: afterReceiving,
  })
}

const trainingSessionPresenceSheet = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/trainingSessionPresenceSheet/${sessionId}`,
    afterReceivingHook: afterReceiving,
  })
}

const updatePresenceSheet = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, participants) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updatePresenceSheet/${sessionId}`,
    data: {participants},
    afterReceivingHook: afterReceiving,
  })
}

const closePresenceSheet = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, participants) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/closePresenceSheet/${sessionId}`,
    data: { participants },
    afterReceivingHook: afterReceiving,
  })
}

const reopenPresenceSheet = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/reopenPresenceSheet/${sessionId}`,
    afterReceivingHook: afterReceiving,
  })
}


const exportTrainingSessionsToExcel = (axios: IFactoryFunctionRequest<any, any >) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false
  })
}

const getEvaluationRemindersPreview = (axios: IFactoryFunctionRequest<any, any>) => (startDate?: string, endDate?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendEvaluationReminders`,
    query: { startDate, endDate },
  })
}

const sendEvaluationReminders = (axios: IFactoryFunctionRequest<any, any>) => (startDate?: string, endDate?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendEvaluationReminders`,
    query: { send: 'true', startDate, endDate },
  })
}

const getSessionEvaluationRemindersPreview = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/sendEvaluationReminders`,
  })
}

const sendSessionEvaluationReminders = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/sendEvaluationReminders`,
    query: { send: 'true' },
  })
}

const sendParticipantEvaluationReminder = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, resourceId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/participant/${resourceId}/sendEvaluationReminder`,
  })
}

const sendPresenceSheetReminders = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, resourceIds: string[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/sendPresenceSheetReminders`,
    data: { resourceIds },
  })
}

const sendParticipantMeeting = (axios: IFactoryFunctionRequest<any, any>) => (
  sessionId: string,
  resourceId: string,
  meetingId: string,
  options: { force?: boolean } = {}
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/participant/${resourceId}/meeting/${meetingId}/sendMeeting`,
    query: options.force ? { force: 'true' } : undefined,
  })
}

const sendMeetingToParticipants = (axios: IFactoryFunctionRequest<any, any>) => (
  sessionId: string,
  meetingId: string,
  resourceIds: string[],
  options: { force?: boolean } = {}
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/meeting/${meetingId}/sendMeeting`,
    data: { resourceIds },
    query: options.force ? { force: 'true' } : undefined,
  })
}

const updateMeetingAttendeesStatus = (axios: IFactoryFunctionRequest<any, any>) => (
  sessionId: string,
  meetingId: string,
  resourceIds: string[],
  status: 'ACCEPTED' | 'DECLINED',
) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/meeting/${meetingId}/attendees/status`,
    data: { resourceIds, status },
  })
}

const registerToSession = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string, resourceIds: string[], sendInvites: boolean = false) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/register`,
    data: { resourceIds, sendInvites },
  })
}

const removeParticipant = (axios: IFactoryFunctionRequest<any, any>) => (
  sessionId: string,
  participantId: string,
  options: { participantIds?: string[] } = {}
) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/participant/${participantId}`,
    data: options.participantIds?.length ? { participantIds: options.participantIds } : undefined,
  })
}

const softDelete = (axios: IFactoryFunctionRequest) => (trainingSessionId: string): IFactoryResponse<{ success: boolean, message: string }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}`,
  })
}

export type AddParticipantsMeta = {
  source?: string
  wishes?: { resourceId: string, evaluationId: string }[]
}

const addParticipants = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, resourceIds: string[], meta: AddParticipantsMeta = {}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/addParticipants`,
    data: { resourceIds, ...meta },
  })
}

const retrieveTrainingSessionEdit = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/edit`,
  })
}

const updateTrainingSession = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, data: any) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

// Budget endpoints
const syncBudgetFromTraining = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/budget/syncFromTraining`,
  })
}

const addBudgetLine = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, data: any) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/budget`,
    data,
  })
}

const updateBudgetLine = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, lineId: string, data: any) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/budget/${lineId}`,
    data,
  })
}

const deleteBudgetLine = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, lineId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}/budget/${lineId}`,
  })
}

// Comments endpoints
const addCommentToTrainingSession = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, comment: any) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addComment/${trainingSessionId}`,
    data: comment,
    beforeSendingHook: beforeSendingComment,
    afterReceivingHook: afterReceivingCommentResponse
  })
} 

const updateCommentToTrainingSession = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, commentId: string, comment: {content: string, taggedPopulated: any[]}) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateComment/${trainingSessionId}/${commentId}`,
    data: comment,
    beforeSendingHook: beforeSendingComment,
  })
}

const deleteCommentToTrainingSession = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string, commentId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteComment/${trainingSessionId}/${commentId}`,
  })
}

const getBase64CandidatePdf = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/pdf/getPresenceSheet/${trainingSessionId}`,
  })
}

const getTrainingSession = (axios: IFactoryFunctionRequest<any, any>) => (trainingSessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${trainingSessionId}`,
    afterReceivingHook: afterReceiving,
  })
}

const getTrainingSessionEvaluationStats = (axios: IFactoryFunctionRequest<any, any>) => (sessionId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/session/${sessionId}/evaluationStats`,
    afterReceivingHook: afterReceiving,
  })
}

const getTrainingEvaluationStats = (axios: IFactoryFunctionRequest<any, any>) => (trainingId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/training/${trainingId}/evaluationStats`,
    afterReceivingHook: afterReceiving,
  })
}

const createTrainingSession = (axios: IFactoryFunctionRequest<ITrainingSession, INewTrainingSessionPage>) => (trainingSession: INewTrainingSessionPage) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/createTrainingSession`,
    data: trainingSession,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

export default (axios: AxiosInstance) => ({
  getAll: getAll(useAxios(axios)),
  updateOne: updateOne(useAxios(axios)),
  create: create(useAxios(axios)),
  updateParticipant: updateParticipant(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  updateCheckItem: updateCheckItem(useAxios(axios)),
  addCheckItem: addCheckItem(useAxios(axios)),
  sendReminder: sendReminder(useAxios(axios)),
  sendTrainingMessage: sendTrainingMessage(useAxios(axios)),
  evaluationIntervalForResource: evaluationIntervalForResource(useAxios(axios)),
  inTheFuture: inTheFuture(useAxios(axios)),
  myTrainings: myTrainings(useAxios(axios)),
  resourceProfileTimeline: resourceProfileTimeline(useAxios(axios)),
  retreiveTrainingEvaluation: retreiveTrainingEvaluation(useAxios(axios)),
  fillInTrainingSessionEvaluation: fillInTrainingSessionEvaluation(useAxios(axios)),
  trainingSessionCalendar: trainingSessionCalendar(useAxios(axios)),
  getSessionModalInformation: getSessionModalInformation(useAxios(axios)),
  trainingSessionPresenceSheet: trainingSessionPresenceSheet(useAxios(axios)),
  updatePresenceSheet: updatePresenceSheet(useAxios(axios)),
  closePresenceSheet: closePresenceSheet(useAxios(axios)),
  reopenPresenceSheet: reopenPresenceSheet(useAxios(axios)),
  retreiveSessionsStats: retreiveSessionsStats(useAxios(axios)),
  exportTrainingSessionsToExcel: exportTrainingSessionsToExcel(useAxios(axios)),
  getEvaluationRemindersPreview: getEvaluationRemindersPreview(useAxios(axios)),
  sendEvaluationReminders: sendEvaluationReminders(useAxios(axios)),
  getSessionEvaluationRemindersPreview: getSessionEvaluationRemindersPreview(useAxios(axios)),
  sendSessionEvaluationReminders: sendSessionEvaluationReminders(useAxios(axios)),
  sendParticipantEvaluationReminder: sendParticipantEvaluationReminder(useAxios(axios)),
  sendPresenceSheetReminders: sendPresenceSheetReminders(useAxios(axios)),
  sendParticipantMeeting: sendParticipantMeeting(useAxios(axios)),
  sendMeetingToParticipants: sendMeetingToParticipants(useAxios(axios)),
  updateMeetingAttendeesStatus: updateMeetingAttendeesStatus(useAxios(axios)),
  registerToSession: registerToSession(useAxios(axios)),
  removeParticipant: removeParticipant(useAxios(axios)),
  softDelete: softDelete(useAxios(axios)),
  addParticipants: addParticipants(useAxios(axios)),
  retrieveTrainingSessionEdit: retrieveTrainingSessionEdit(useAxios(axios)),
  updateTrainingSession: updateTrainingSession(useAxios(axios)),
  // Participants
  getBase64CandidatePdf: getBase64CandidatePdf(useAxios(axios)),
  // Budget
  syncBudgetFromTraining: syncBudgetFromTraining(useAxios(axios)),
  addBudgetLine: addBudgetLine(useAxios(axios)),
  updateBudgetLine: updateBudgetLine(useAxios(axios)),
  deleteBudgetLine: deleteBudgetLine(useAxios(axios)),
  // Comments
  addCommentToTrainingSession: addCommentToTrainingSession(useAxios(axios)),
  updateCommentToTrainingSession: updateCommentToTrainingSession(useAxios(axios)),
  deleteCommentToTrainingSession: deleteCommentToTrainingSession(useAxios(axios)),
  // Training session
  getTrainingSession: getTrainingSession(useAxios(axios)),
  getTrainingSessionEvaluationStats: getTrainingSessionEvaluationStats(useAxios(axios)),
  getTrainingEvaluationStats: getTrainingEvaluationStats(useAxios(axios)),
  createTrainingSession: createTrainingSession(useAxios(axios)),
})
